import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography, Collapse, List, ListItem, ListItemIcon, Link } from '@mui/material';
import { WarningAmber, ExpandMore, ExpandLess, FiberManualRecord } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import useStyles from './style';

const HEADER_ID = 'reconciliation-warnings-header';

const ReconciliationWarnings = ({ warnings = [], isExpanded = true, onToggle, onAssetLinkClick }) => {
  const classes = useStyles();

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onToggle();
      }
    },
    [onToggle],
  );

  const handleAssetLinkClick = useCallback(() => {
    onAssetLinkClick?.();
  }, [onAssetLinkClick]);

  if (!warnings || warnings.length === 0) {
    return null;
  }

  const warningCount = warnings.length;
  const headerText = `${warningCount} Warning${warningCount !== 1 ? 's' : ''} Found At Sublocation`;

  return (
    <Paper elevation={0} className={classes.warningContainer}>
      <Box
        id={HEADER_ID}
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        className={classes.warningHeader}
        aria-expanded={isExpanded}
        aria-controls="reconciliation-warnings-content"
      >
        <Box className={classes.warningHeaderContent}>
          <WarningAmber className={classes.warningIcon} />
          <Typography variant="body1" className={classes.warningTitle}>
            {headerText}
          </Typography>
        </Box>
        {isExpanded ? (
          <ExpandLess className={classes.warningExpandIcon} />
        ) : (
          <ExpandMore className={classes.warningExpandIcon} />
        )}
      </Box>

      <Collapse in={isExpanded} id="reconciliation-warnings-content" aria-labelledby={HEADER_ID}>
        <Box className={classes.warningListContainer}>
          <List dense disablePadding>
            {warnings.map((warning) => {
              const { id, message, assetId, assetLabel } = warning;
              const hasLinkedAsset =
                assetId && assetLabel && typeof message === 'string' && message.includes(assetLabel);

              if (!hasLinkedAsset) {
                return (
                  <ListItem key={id} disableGutters className={classes.warningListItem}>
                    <ListItemIcon className={classes.warningBullet}>
                      <FiberManualRecord className={classes.warningBulletIcon} />
                    </ListItemIcon>
                    <Typography variant="body2" className={classes.warningMessage}>
                      {message}
                    </Typography>
                  </ListItem>
                );
              }

              const idx = message.indexOf(assetLabel);
              const beforeAsset = message.slice(0, idx);
              const afterAsset = message.slice(idx + assetLabel.length);

              return (
                <ListItem key={id} disableGutters className={classes.warningListItem}>
                  <ListItemIcon className={classes.warningBullet}>
                    <FiberManualRecord className={classes.warningBulletIcon} />
                  </ListItemIcon>
                  <Typography variant="body2" component="span" className={classes.warningMessage}>
                    {beforeAsset}
                    <Link
                      component={RouterLink}
                      to={`/machines/${assetId}`}
                      underline="always"
                      onClick={handleAssetLinkClick}
                    >
                      {assetLabel}
                    </Link>
                    {afterAsset}
                  </Typography>
                </ListItem>
              );
            })}
          </List>

          <Typography variant="caption" color="textSecondary" className={classes.warningInfoNote}>
            These warnings are informational and do not block the Finalize action.
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

ReconciliationWarnings.propTypes = {
  warnings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      assetId: PropTypes.string,
      assetLabel: PropTypes.string,
      sublocation: PropTypes.string,
      warningType: PropTypes.string,
    }),
  ),
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onAssetLinkClick: PropTypes.func,
};

ReconciliationWarnings.defaultProps = {
  warnings: [],
  isExpanded: true,
};

export default ReconciliationWarnings;
